/**
 * Client utility functions
 * 
 * Provides utilities for mapping and transforming client data
 * between API format and dashboard display format.
 */

import { DEFAULT_USER_AVATAR } from '@/shared/constants/avatars';

/**
 * Client data structure from matchmaking stored procedure API.
 * Feature 054: Renamed invitedVia → invitationMethod to align
 * with the unified ClientRelationship model (InvitationMethod enum).
 */
export type AdvisorClientApi = {
  id: string;
  name: string;
  email: string;
  firstName?: string;
  lastName?: string;
  profilePhoto?: string | null;
  invitationMethod?: string;
  connectedAt?: string | Date | null;
  ageRange?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  createdAt?: string | null;
  relative?: string | null;
};

/**
 * Client profile data for dashboard display
 * Contains formatted profile information for UI rendering
 */
export type DashboardClientProfile = {
  name: string;
  email: string | null;
  age?: number | null;
  ageRange?: string | null;
  location: {
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  };
  relationship?: string | null;
  reasonForJoining?: string | null;
};

/**
 * Complete client data structure for dashboard
 * Combines client ID, avatar, and profile information
 */
export type DashboardClient = {
  id: string;
  avatar: string;
  profile: DashboardClientProfile;
};

/**
 * Converts numeric age to age range label
 * 
 * Groups ages into 10-year ranges (0-9, 10-19, etc.)
 * Ages 90+ are grouped together. Single ages return as-is.
 * 
 * @param age - Numeric age value
 * @returns Age range label string (e.g., "20-29", "90+", or exact age)
 */
export const getAgeRangeLabel = (age: number) => {
  if (!Number.isFinite(age)) return '-';
  if (age >= 90) return '90+';
  const lowerBound = Math.max(0, Math.floor(age / 10) * 10);
  const upperBound = lowerBound + 9;
  if (lowerBound === upperBound) {
    return `${age}`;
  }
  return `${lowerBound}-${upperBound}`;
};

/**
 * Maps API client data to dashboard client format
 * 
 * Transforms raw API client data into the format expected by dashboard components.
 * Handles missing data gracefully with fallbacks for name, ID, and avatar.
 * 
 * @param apiClient - Raw client data from API
 * @returns Formatted dashboard client object
 */
export const mapApiClientToDashboardClient = (apiClient: AdvisorClientApi): DashboardClient => {
  const fallbackName = 
    apiClient.name || 
    (apiClient.firstName || apiClient.lastName 
      ? `${apiClient.firstName || ''} ${apiClient.lastName || ''}`.trim()
      : 'Unnamed Client');
  const safeId = apiClient.id || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const avatar = apiClient.profilePhoto || DEFAULT_USER_AVATAR;
  
  return {
    id: safeId,
    avatar: avatar,
    profile: {
      name: fallbackName,
      email: apiClient.email || null,
      age: null,
      ageRange: apiClient.ageRange || null,
      location: {
        city: apiClient.city || null,
        state: apiClient.state || null,
        zip: apiClient.zipCode || null,
      },
      relationship: apiClient.relative || null,
      reasonForJoining: apiClient.relative ? `Supporting ${apiClient.relative}` : null,
    },
  };
};

